import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFinancialReport } from '@/presentation/hooks/useFinancialReport';
import { useClinicReport } from '@/presentation/hooks/useClinicReport';
import { useTenants } from '@/presentation/hooks/useTenants';
import { usePlans } from '@/presentation/hooks/usePlans';
import { Button } from '@/presentation/components/common';

function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const escape = (value: string | number) => {
    const text = String(value ?? '');
    if (/[",\n;]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };
  const lines = [headers.map(escape).join(','), ...rows.map((row) => row.map(escape).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const { report: financial, isLoading: financialLoading, error: financialError } = useFinancialReport();
  const { report: clinics, isLoading: clinicsLoading, error: clinicsError } = useClinicReport();
  const { tenants } = useTenants();
  const { plans } = usePlans();
  const [activeTab, setActiveTab] = useState<'financial' | 'tenants'>('financial');

  const tenantsByPlan = useMemo(
    () =>
      plans.map((plan) => ({
        planName: plan.nombre,
        count: tenants.filter((tenant) => tenant.planAdminId === plan.id).length,
      })),
    [plans, tenants]
  );

  const tenantsByStatus = useMemo(
    () =>
      [
        { status: 'Abonado', count: tenants.filter((t) => t.estadoPagoDescripcion === 'Abonado').length },
        {
          status: 'Mora',
          count: tenants.filter(
            (t) => t.estadoPagoDescripcion === 'Mora' || t.estadoPagoDescripcion === 'Abonado con Mora'
          ).length,
        },
        {
          status: 'Pendiente pago',
          count: tenants.filter(
            (t) => t.estadoPagoDescripcion === 'Pendiente pago' || t.estadoPagoDescripcion === 'Impago'
          ).length,
        },
        {
          status: 'Bonificado',
          count: tenants.filter(
            (t) => t.estadoPagoDescripcion === 'Bonificado' || t.estadoPagoDescripcion === 'Gratis'
          ).length,
        },
      ].filter((item) => item.count > 0),
    [tenants]
  );

  const handleExport = () => {
    const date = new Date().toISOString().split('T')[0];
    if (activeTab === 'financial' && financial) {
      downloadCsv(
        `reporte-financiero-${date}.csv`,
        ['metric', 'value'],
        [
          ['totalRevenue', financial.totalRevenue],
          ['pendingRevenue', financial.pendingRevenue],
          ['overdueRevenue', financial.overdueRevenue],
          ['totalAccounts', financial.totalAccounts],
          ['paidAccounts', financial.paidAccounts],
          ['pendingAccounts', financial.pendingAccounts],
          ['overdueAccounts', financial.overdueAccounts],
          ...financial.monthlyRevenue.map((item) => [`monthlyRevenue:${item.month}`, item.revenue]),
          ...financial.accountStatusBreakdown.map(
            (item) => [`status:${item.status}:count`, item.count] as (string | number)[]
          ),
          ...financial.accountStatusBreakdown.map(
            (item) => [`status:${item.status}:amount`, item.totalAmount] as (string | number)[]
          ),
        ]
      );
      return;
    }

    downloadCsv(
      `reporte-tenants-${date}.csv`,
      ['metric', 'value'],
      [
        ['totalClinics', clinics?.totalClinics ?? tenants.length],
        ['activeClinics', clinics?.activeClinics ?? tenants.filter((t) => t.activo).length],
        ['inactiveClinics', clinics?.inactiveClinics ?? tenants.filter((t) => !t.activo).length],
        ...tenantsByPlan.map((item) => [`plan:${item.planName}`, item.count] as (string | number)[]),
        ...tenantsByStatus.map((item) => [`status:${item.status}`, item.count] as (string | number)[]),
      ]
    );
  };

  const isLoading = financialLoading || clinicsLoading;
  const totalTenants = clinics?.totalClinics ?? tenants.length;
  const activeTenants = clinics?.activeClinics ?? tenants.filter((t) => t.activo).length;
  const inactiveTenants = clinics?.inactiveClinics ?? tenants.filter((t) => !t.activo).length;

  if (isLoading && !financial && !clinics) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.reports.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">
            {t('pages.reports.subtitle')}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport} disabled={!financial && !clinics} className="w-full sm:w-auto">
          {t('pages.reports.exportCsv')}
        </Button>
      </div>

      {(financialError || clinicsError) && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            {financialError?.message || clinicsError?.message}
          </p>
        </div>
      )}

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-3 overflow-x-auto sm:gap-6">
          <button
            onClick={() => setActiveTab('financial')}
            className={`py-2.5 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'financial'
                ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {t('pages.reports.financial')}
          </button>
          <button
            onClick={() => setActiveTab('tenants')}
            className={`py-2.5 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'tenants'
                ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {t('pages.reports.tenants')}
          </button>
        </nav>
      </div>

      {activeTab === 'financial' && financial && (
        <div className="space-y-4 xl:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 lg:gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4 border-l-4 border-green-500">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('pages.reports.totalRevenue')}
              </p>
              <p className="text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 dark:text-white mt-1">
                ${financial.totalRevenue.toLocaleString()}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4 border-l-4 border-teal-500">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('pages.reports.totalAccounts')}
              </p>
              <p className="text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 dark:text-white mt-1">
                {financial.totalAccounts}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4 border-l-4 border-yellow-500">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('pages.reports.pendingAccounts')}
              </p>
              <p className="text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 dark:text-white mt-1">
                {financial.pendingAccounts}
              </p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                ${financial.pendingRevenue.toLocaleString()}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4 border-l-4 border-red-500">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('pages.reports.overdueAccounts')}
              </p>
              <p className="text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 dark:text-white mt-1">
                {financial.overdueAccounts}
              </p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                ${financial.overdueRevenue.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                {t('pages.reports.accountStatus')}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('pages.reports.paid')}:</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    {financial.paidAccounts}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('pages.reports.pending')}:</span>
                  <span className="font-semibold text-yellow-600 dark:text-yellow-400">
                    {financial.pendingAccounts}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('pages.reports.overdue')}:</span>
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    {financial.overdueAccounts}
                  </span>
                </div>
                {financial.accountStatusBreakdown.length > 0 && (
                  <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                    {financial.accountStatusBreakdown.map((item) => (
                      <div key={item.status} className="flex justify-between items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400 capitalize">{item.status}</span>
                        <span className="text-gray-900 dark:text-white">
                          {item.count} · ${item.totalAmount.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                {t('pages.reports.monthlyRevenue')}
              </h3>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {financial.monthlyRevenue.length > 0 ? (
                  [...financial.monthlyRevenue].reverse().map((item) => (
                    <div key={item.month} className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">{item.month}:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        ${item.revenue.toLocaleString()}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">{t('pages.reports.noData')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'tenants' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4 border-l-4 border-teal-500">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('pages.reports.totalTenants')}
              </p>
              <p className="text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 dark:text-white mt-1">
                {totalTenants}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4 border-l-4 border-green-500">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('pages.reports.activeTenants')}
              </p>
              <p className="text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 dark:text-white mt-1">
                {activeTenants}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4 border-l-4 border-red-500">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('pages.reports.inactiveTenants')}
              </p>
              <p className="text-lg lg:text-xl xl:text-2xl font-bold text-slate-900 dark:text-white mt-1">
                {inactiveTenants}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                {t('pages.reports.tenantsByPlan')}
              </h3>
              <div className="space-y-3">
                {tenantsByPlan.length > 0 ? (
                  tenantsByPlan.map((item) => (
                    <div key={item.planName} className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">{item.planName}:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{item.count}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">{t('pages.reports.noData')}</p>
                )}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 lg:p-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                {t('pages.reports.tenantsByStatus')}
              </h3>
              <div className="space-y-3">
                {tenantsByStatus.length > 0 ? (
                  tenantsByStatus.map((item) => (
                    <div key={item.status} className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">{item.status}:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{item.count}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">{t('pages.reports.noData')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
